import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Code2, Maximize2, Monitor, RotateCw, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useHermes } from "@/components/hermes/HermesProvider";
import { ApiError } from "@/lib/api";
import {
  isPreviewNavMessage,
  missingPreviewPageHtml,
  parsePreviewHref,
  pathForPreviewSlug,
} from "@/lib/brand-site-preview-nav";
import { createHermesApi } from "@/lib/hermes-api";
import {
  buildHermesHtmlPreviewSrcDoc,
  findHermesPreviewBySlug,
  HERMES_HTML_PREVIEW_FOOTNOTE,
  HERMES_HTML_PREVIEW_SANDBOX,
  type HermesHtmlPreviewSummary,
} from "@/lib/hermes-html-preview";
import {
  hermesMessageBrandSiteWriteFromPayload,
  isHermesPreviewAlreadyApplied,
  type HermesBrandSiteWritePayload,
} from "@/lib/hermes-brand-site-write";
import { cn } from "@/lib/utils";

type Viewport = "desktop" | "mobile";
type FullscreenPane = "preview" | "source";

type HermesHtmlPreviewCardProps = {
  accountId: string;
  preview: HermesHtmlPreviewSummary;
  /** 同条消息内其它页面预览，用于站内链接切换 */
  relatedPreviews?: readonly HermesHtmlPreviewSummary[];
  /** 服务端判定的改页写入结果（同条助手消息 tool_payload） */
  brandSiteWrite?: HermesBrandSiteWritePayload | null;
};

const HOME_SLUG = "index";

const CARD_FOOTNOTE = "点「全屏预览」看效果与源码；满意后点「写入草稿」。" as const;

export function HermesHtmlPreviewCard({
  accountId,
  preview,
  relatedPreviews = [],
  brandSiteWrite = null,
}: HermesHtmlPreviewCardProps) {
  const { notifyBrandSiteDraftMutated } = useHermes();
  const fullIframeRef = useRef<HTMLIFrameElement>(null);

  const [viewingId, setViewingId] = useState(preview.id);
  const [html, setHtml] = useState<string | null>(null);
  /** 全屏「源码」与乐观写入草稿用；详情仅在打开全屏后拉取 */
  const [seedHtml, setSeedHtml] = useState<string | null>(null);
  const [assetBaseUrl, setAssetBaseUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [fullscreenPane, setFullscreenPane] = useState<FullscreenPane>("preview");
  const [viewport, setViewport] = useState<Viewport>("desktop");
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(
    () => isHermesPreviewAlreadyApplied(preview.id, preview.slug, brandSiteWrite),
  );
  const [reloadToken, setReloadToken] = useState(0);
  const [missingPath, setMissingPath] = useState<string | null>(null);

  const catalog = useMemo(() => {
    const byId = new Map<number, HermesHtmlPreviewSummary>();
    byId.set(preview.id, preview);
    for (const item of relatedPreviews) byId.set(item.id, item);
    return [...byId.values()];
  }, [preview, relatedPreviews]);

  const viewingPreview = useMemo(
    () => catalog.find((item) => item.id === viewingId) ?? preview,
    [catalog, preview, viewingId],
  );

  const title = viewingPreview.title?.trim() || viewingPreview.slug || "页面预览";
  const cardTitle = preview.title?.trim() || preview.slug || "页面预览";
  const displayPath = useMemo(() => {
    if (missingPath) return missingPath;
    return pathForPreviewSlug(viewingPreview.slug || HOME_SLUG, HOME_SLUG);
  }, [missingPath, viewingPreview.slug]);

  const previewHtml = useMemo(() => {
    if (missingPath) {
      return buildHermesHtmlPreviewSrcDoc(missingPreviewPageHtml(missingPath), null);
    }
    if (!html) return "";
    return buildHermesHtmlPreviewSrcDoc(html, assetBaseUrl);
  }, [assetBaseUrl, html, missingPath]);

  const reloadPreview = useCallback(() => {
    setMissingPath(null);
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    setViewingId(preview.id);
    setMissingPath(null);
    setApplied(isHermesPreviewAlreadyApplied(preview.id, preview.slug, brandSiteWrite));
    setSeedHtml(null);
    setHtml(null);
    setAssetBaseUrl(null);
    setLoadError(null);
    setFullscreenPane("preview");
  }, [preview.id, brandSiteWrite, preview.slug]);

  // 仅全屏打开时拉详情（避免聊天区多卡同时展开 Liquid 拖慢整站）
  useEffect(() => {
    if (!fullscreenOpen) return;

    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    async function load() {
      try {
        const hermes = createHermesApi(accountId);
        const { preview: detail } = await hermes.getHtmlPreview(viewingId);
        if (cancelled) return;
        const nextHtml = typeof detail.html === "string" ? detail.html : "";
        const nextSource =
          typeof detail.source_html === "string" && detail.source_html.length > 0
            ? detail.source_html
            : nextHtml;
        setHtml(nextHtml);
        if (viewingId === preview.id) setSeedHtml(nextSource);
        setAssetBaseUrl(
          typeof detail.asset_base_url === "string" && detail.asset_base_url.trim()
            ? detail.asset_base_url.trim()
            : null,
        );
      } catch {
        if (!cancelled) {
          setLoadError("预览加载失败，请稍后重试");
          setHtml(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [accountId, fullscreenOpen, preview.id, viewingId, reloadToken]);

  const navigateToSlug = useCallback(
    (slug: string, path: string) => {
      const hit = findHermesPreviewBySlug(catalog, slug);
      if (hit) {
        setMissingPath(null);
        setViewingId(hit.id);
        setFullscreenPane("preview");
        return;
      }
      setMissingPath(path);
    },
    [catalog],
  );

  const handlePreviewHref = useCallback(
    (href: string) => {
      const parsed = parsePreviewHref(href, HOME_SLUG);
      if (parsed.kind === "hash" || parsed.kind === "ignore") return;
      if (parsed.kind === "external") {
        window.open(parsed.url, "_blank", "noopener,noreferrer");
        return;
      }
      navigateToSlug(parsed.slug, parsed.path);
    },
    [navigateToSlug],
  );

  useEffect(() => {
    if (!fullscreenOpen) return;

    function onMessage(event: MessageEvent) {
      if (!isPreviewNavMessage(event.data)) return;
      if (event.source !== fullIframeRef.current?.contentWindow) return;
      handlePreviewHref(event.data.href);
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [fullscreenOpen, handlePreviewHref]);

  async function handleApply() {
    if (applying || applied) return;
    setApplying(true);
    try {
      const hermes = createHermesApi(accountId);
      const result = await hermes.applyHtmlPreview(preview.id);
      setApplied(true);
      const appliedSource =
        (typeof result.preview?.source_html === "string" && result.preview.source_html) ||
        (typeof result.page?.html_content === "string" && result.page.html_content) ||
        seedHtml ||
        "";
      if (appliedSource) setSeedHtml(appliedSource);
      const slug =
        (typeof result.page?.slug === "string" && result.page.slug.trim()) || preview.slug;
      notifyBrandSiteDraftMutated({
        slug,
        page: result.page
          ? {
              id: typeof result.page.id === "number" ? result.page.id : undefined,
              slug,
              title: typeof result.page.title === "string" ? result.page.title : preview.title,
              html_content:
                typeof result.page.html_content === "string"
                  ? result.page.html_content
                  : appliedSource,
              is_home:
                typeof result.page.is_home === "boolean" ? result.page.is_home : undefined,
            }
          : { slug, html_content: appliedSource, title: preview.title },
      });
      toast.success("已写入草稿");
    } catch (caught) {
      const message = caught instanceof ApiError ? caught.message : "写入草稿失败，请稍后重试";
      toast.error(message);
    } finally {
      setApplying(false);
    }
  }

  return (
    <div className="w-full overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-sm">
      <div className="flex items-start justify-between gap-2 px-3 py-2.5">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{cardTitle}</p>
          <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">{preview.slug}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <Button
            type="button"
            size="sm"
            className="h-8 px-2.5 text-xs"
            disabled={applying || applied}
            onClick={() => void handleApply()}
          >
            {applied ? "已写入" : applying ? "写入中…" : "写入草稿"}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 shrink-0 gap-1.5 px-2.5 text-xs"
            onClick={() => {
              setFullscreenPane("preview");
              setFullscreenOpen(true);
            }}
          >
            <Maximize2 className="size-3.5" />
            全屏预览
          </Button>
        </div>
      </div>

      <p className="border-t border-border/70 px-3 py-2 text-[11px] leading-relaxed text-muted-foreground">
        {CARD_FOOTNOTE}
      </p>

      <Dialog
        open={fullscreenOpen}
        onOpenChange={(open) => {
          setFullscreenOpen(open);
          if (!open) setFullscreenPane("preview");
        }}
      >
        <DialogContent
          className="flex h-[min(92vh,880px)] w-full max-w-[min(96vw,1100px)] flex-col gap-3 p-4 sm:max-w-[min(96vw,1100px)]"
          showCloseButton
        >
          <DialogHeader className="shrink-0 space-y-2 pr-8">
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription className="sr-only">当前路径 {displayPath}</DialogDescription>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex min-w-0 flex-1 items-center gap-1 rounded-md border border-border bg-muted/40 px-1.5 py-1">
                <ViewportButton label="刷新预览" pressed={false} onClick={reloadPreview}>
                  <RotateCw className={cn("size-3.5", loading && "animate-spin")} />
                </ViewportButton>
                <input
                  value={displayPath}
                  readOnly
                  aria-label="预览地址"
                  title={displayPath}
                  className="h-7 min-w-0 flex-1 rounded-md border border-border bg-background px-2 font-mono text-xs text-foreground outline-none"
                />
                <div className="flex items-center gap-0.5 rounded-md border border-border p-0.5">
                  <ViewportButton
                    label="桌面预览"
                    pressed={viewport === "desktop"}
                    onClick={() => setViewport("desktop")}
                  >
                    <Monitor className="size-3.5" />
                  </ViewportButton>
                  <ViewportButton
                    label="手机预览"
                    pressed={viewport === "mobile"}
                    onClick={() => setViewport("mobile")}
                  >
                    <Smartphone className="size-3.5" />
                  </ViewportButton>
                </div>
              </div>
              <div className="flex items-center gap-0.5 rounded-md border border-border p-0.5">
                <PaneButton
                  label="看界面"
                  pressed={fullscreenPane === "preview"}
                  onClick={() => setFullscreenPane("preview")}
                >
                  <Maximize2 className="size-3.5" />
                  界面
                </PaneButton>
                <PaneButton
                  label="看源码"
                  pressed={fullscreenPane === "source"}
                  onClick={() => setFullscreenPane("source")}
                >
                  <Code2 className="size-3.5" />
                  源码
                </PaneButton>
              </div>
            </div>
          </DialogHeader>

          <div className="flex min-h-0 flex-1 items-stretch justify-center overflow-hidden rounded-lg border border-border bg-neutral-200/70">
            {fullscreenPane === "source" ? (
              <pre className="h-full w-full overflow-auto bg-background p-4 font-mono text-xs whitespace-pre-wrap break-all text-foreground">
                {loading && viewingId === preview.id
                  ? "加载中…"
                  : loadError && viewingId === preview.id
                    ? loadError
                    : viewingId === preview.id
                      ? (seedHtml ?? "（空）")
                      : "切换回本页卡片对应页面后可查看写入源码。当前为站内跳转页预览。"}
              </pre>
            ) : loading && !missingPath ? (
              <div className="flex items-center justify-center px-4 text-sm text-muted-foreground">
                加载预览中…
              </div>
            ) : previewHtml ? (
              <iframe
                ref={fullIframeRef}
                key={`${viewingId}-${reloadToken}-${missingPath ?? "ok"}`}
                title={`${title} 全屏预览`}
                sandbox={HERMES_HTML_PREVIEW_SANDBOX}
                srcDoc={previewHtml}
                className={cn(
                  "h-full bg-white",
                  viewport === "mobile" ? "mx-auto w-[390px] max-w-full shadow-lg" : "w-full",
                )}
              />
            ) : (
              <div className="flex items-center justify-center px-4 text-sm text-muted-foreground">
                {loadError ?? "暂无预览内容"}
              </div>
            )}
          </div>

          <p className="shrink-0 text-[11px] text-muted-foreground">{HERMES_HTML_PREVIEW_FOOTNOTE}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ViewportButton({
  label,
  pressed,
  onClick,
  children,
}: {
  label: string;
  pressed: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={pressed}
      title={label}
      onClick={onClick}
      className={cn(
        "rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground",
        pressed && "bg-muted text-foreground",
      )}
    >
      {children}
    </button>
  );
}

function PaneButton({
  label,
  pressed,
  onClick,
  children,
}: {
  label: string;
  pressed: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={pressed}
      title={label}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1 rounded px-2 py-1.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground",
        pressed && "bg-muted font-medium text-foreground",
      )}
    >
      {children}
    </button>
  );
}
